import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronRight,
  HeartPulse,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  X,
} from "lucide-react";
import "./ChatBot.css";

const phone = "+918925969546";
const email = "deantmch@takshashilauniv.ac.in";

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("home");
  const trigger = useRef(null);
  const closeButton = useRef(null);

  const close = () => {
    setIsOpen(false);
    trigger.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;
    closeButton.current?.focus();
    const onEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [isOpen]);

  return (
    <div className="college-help">
      <button
        ref={trigger}
        className="college-help-trigger"
        type="button"
        aria-label={
          isOpen ? "Close college assistant" : "Open college assistant"
        }
        aria-expanded={isOpen}
        aria-controls="college-assistant"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <span className="college-help-trigger-icon" key={isOpen ? 'close' : 'chat'} aria-hidden="true">
          {isOpen ? <X size={22} /> : <MessageCircle size={23} />}
        </span>
        {!isOpen && <span className="college-help-trigger-label" aria-hidden="true">How can we help?</span>}
      </button>
      {isOpen && (
        <section
          id="college-assistant"
          className="college-help-panel"
          aria-label="College assistant"
        >
          <header className="college-help-header">
            <span className="college-help-mark">
              <img
                src="/assets/images/logo/logo.svg"
                alt=""
                aria-hidden="true"
              />
            </span>
            <div>
              <span className="college-help-brand">TAKSHASHILA</span>
              <p>College & hospital support</p>
            </div>
            <button
              ref={closeButton}
              type="button"
              className="college-help-close"
              aria-label="Close support panel"
              onClick={close}
            >
              <X size={19} />
            </button>
          </header>
          <div className="college-help-body">
            {view === "home" ? (
              <>
                <p className="college-help-eyebrow">HERE TO HELP</p>
                <h2>
                  Hello.
                  <br />
                  How can we help?
                </h2>
                <p className="college-help-intro">
                  Connect with Takshashila Medical College & Hospital.
                </p>
                <div className="college-help-options">
                  <a className="college-help-option" href={`tel:${phone}`}>
                    <span className="college-help-icon">
                      <Phone size={19} />
                    </span>
                    <span>
                      <strong>Call the college</strong>
                      <small>Speak with our team</small>
                    </span>
                    <ArrowUpRight size={17} />
                  </a>
                  <a
                    className="college-help-option"
                    href="https://wa.me/918925969546?text=Hi%20I%20need%20assistance%20from%20Takshashila%20Medical%20College"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="college-help-icon">
                      <MessageCircle size={19} />
                    </span>
                    <span>
                      <strong>Chat on WhatsApp</strong>
                      <small>Continue in WhatsApp</small>
                    </span>
                    <ArrowUpRight size={17} />
                  </a>
                  <button
                    className="college-help-option"
                    type="button"
                    onClick={() => setView("contact")}
                  >
                    <span className="college-help-icon">
                      <MapPin size={19} />
                    </span>
                    <span>
                      <strong>Contact details</strong>
                      <small>Find our address and email</small>
                    </span>
                    <ChevronRight size={17} />
                  </button>
                </div>
                <button
                  className="college-help-emergency"
                  type="button"
                  onClick={() => setView("emergency")}
                >
                  <HeartPulse size={20} />
                  <span>Emergency assistance</span>
                  <ChevronRight size={17} />
                </button>
              </>
            ) : (
              <>
                <button
                  className="college-help-back"
                  type="button"
                  onClick={() => setView("home")}
                >
                  <ArrowLeft size={15} /> All options
                </button>
                <div className="college-help-detail" aria-live="polite">
                  <p className="college-help-eyebrow">
                    {view === "contact" ? "GET IN TOUCH" : "HOSPITAL SUPPORT"}
                  </p>
                  <h2>
                    {view === "contact"
                      ? "Contact our team."
                      : "Emergency assistance."}
                  </h2>
                  {view === "contact" ? (
                    <>
                      <div className="college-help-address">
                        <MapPin size={19} />
                        <p>
                          Takshashila Medical College & Hospital
                          <br />
                          Ongur, Tindivanam Taluk,
                          <br />
                          Villupuram District,
                          <br />
                          Tamil Nadu - 604 305
                        </p>
                      </div>
                      <a
                        className="college-help-detail-link"
                        href={`mailto:${email}`}
                      >
                        <Mail size={18} />
                        <span>{email}</span>
                        <ArrowUpRight size={16} />
                      </a>
                      <a
                        className="college-help-detail-link"
                        href={`tel:${phone}`}
                      >
                        <Phone size={18} />
                        <span>+91 8925969546</span>
                        <ArrowUpRight size={16} />
                      </a>
                    </>
                  ) : (
                    <>
                      <p className="college-help-intro">
                        Emergency services are available 24/7 at our teaching
                        hospital. Call for assistance.
                      </p>
                      <a className="college-help-call" href={`tel:${phone}`}>
                        <Phone size={18} /> +91 8925969546{" "}
                        <ArrowUpRight size={17} />
                      </a>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <footer className="college-help-footer">
            Knowledge. Compassion. Service.
          </footer>
        </section>
      )}
    </div>
  );
}
